import { parseInstantFromApi } from "utils/dateFormatting";

import { isThreadUnreadByReadAt } from "../../script-comment-thread-reads";
import type { DocumentComment } from "../../types";
import type { CommentPanelSort } from "./commentPanelSort.types";
import type { CommentThreadRow } from "./scriptCommentsPanelFilters.types";

export type SortCommentThreadsContext = {
  /** `null` while the viewer's read times are unknown: still loading, or their request failed. */
  readAtByRoot: Record<string, string> | null;
  currentUserId?: string | null;
};

/** Unknown read times rank no thread as unread, so priority falls back to unresolved first. */
function isThreadUnread(thread: CommentThreadRow, context: SortCommentThreadsContext): boolean {
  if (context.readAtByRoot === null) return false;
  return isThreadUnreadByReadAt(
    thread.comment,
    thread.replies,
    context.readAtByRoot[thread.comment.comment_id],
    context.currentUserId
  );
}

function rootCreatedAtMs(comment: DocumentComment): number {
  return parseInstantFromApi(comment.created_at).getTime();
}

function latestReplyMs(replies: DocumentComment[]): number {
  let max = 0;
  for (const reply of replies) {
    max = Math.max(
      max,
      parseInstantFromApi(reply.created_at).getTime(),
      parseInstantFromApi(reply.updated_at).getTime()
    );
  }
  return max;
}

function sortCommentThreadsByNewestReplies(threads: CommentThreadRow[]): CommentThreadRow[] {
  return [...threads].sort((a, b) => {
    const aHasReplies = a.replies.length > 0;
    const bHasReplies = b.replies.length > 0;

    if (aHasReplies !== bHasReplies) {
      return aHasReplies ? -1 : 1;
    }

    if (aHasReplies && bHasReplies) {
      return latestReplyMs(b.replies) - latestReplyMs(a.replies);
    }

    return rootCreatedAtMs(b.comment) - rootCreatedAtMs(a.comment);
  });
}

function threadNeedsAttention(
  thread: CommentThreadRow,
  context: SortCommentThreadsContext
): boolean {
  return isThreadUnread(thread, context) || thread.comment.status === "open";
}

function sortCommentThreadsByPriority(
  threads: CommentThreadRow[],
  context: SortCommentThreadsContext
): CommentThreadRow[] {
  const anyNeedsAttention = threads.some((thread) => threadNeedsAttention(thread, context));
  if (!anyNeedsAttention) {
    return [...threads].sort((a, b) => rootCreatedAtMs(b.comment) - rootCreatedAtMs(a.comment));
  }

  return [...threads].sort((a, b) => {
    const aUnread = isThreadUnread(a, context);
    const bUnread = isThreadUnread(b, context);
    if (aUnread !== bUnread) {
      return aUnread ? -1 : 1;
    }

    const aOpen = a.comment.status === "open" ? 0 : 1;
    const bOpen = b.comment.status === "open" ? 0 : 1;
    if (aOpen !== bOpen) {
      return aOpen - bOpen;
    }

    return rootCreatedAtMs(b.comment) - rootCreatedAtMs(a.comment);
  });
}

export function sortCommentThreads(
  threads: CommentThreadRow[],
  sort: CommentPanelSort,
  context: SortCommentThreadsContext
): CommentThreadRow[] {
  if (sort === "newest") {
    return [...threads].sort((a, b) => rootCreatedAtMs(b.comment) - rootCreatedAtMs(a.comment));
  }

  if (sort === "newest_replies") {
    return sortCommentThreadsByNewestReplies(threads);
  }

  if (sort === "oldest") {
    return [...threads].sort((a, b) => rootCreatedAtMs(a.comment) - rootCreatedAtMs(b.comment));
  }

  return sortCommentThreadsByPriority(threads, context);
}
